/**
 * Letter-size PDF of a rendered sheet (8.5in x 11in), used by the "Guardar PDF" buttons.
 * html2canvas + jsPDF are heavy, so they load on first use. The PDF is an image of
 * the sheet (text is not selectable); the Imprimir button still gives a vector PDF.
 */

const LETTER_WIDTH_IN = 8.5;
const LETTER_HEIGHT_IN = 11;

/** Wait until every image in `el` has loaded (QR codes appear asynchronously) */
async function whenImagesReady(el: HTMLElement, expectQr: boolean, timeoutMs = 4000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const imgs = [...el.querySelectorAll('img')];
    const qrReady = !expectQr || imgs.some((i) => i.alt === 'QR');
    if (qrReady && imgs.every((i) => i.complete)) return;
    await new Promise((r) => setTimeout(r, 50));
  }
}

export async function exportSheetToPdf(el: HTMLElement, fileName: string, expectQr = true) {
  await whenImagesReady(el, expectQr);

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    width: el.offsetWidth,
    height: el.offsetHeight,
    windowWidth: el.offsetWidth,
  });

  // Keep the sheet's proportions: a receipt with many lines can be taller than a letter page
  const ratio = canvas.height / canvas.width;
  let w = LETTER_WIDTH_IN;
  let h = w * ratio;
  if (h > LETTER_HEIGHT_IN) {
    h = LETTER_HEIGHT_IN;
    w = h / ratio;
  }

  const pdf = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait', compress: true });
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', (LETTER_WIDTH_IN - w) / 2, 0, w, h);
  pdf.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
}

/** "Formulario-EPNFP26-1234567.pdf"-style names: ASCII, no spaces */
export function pdfFileName(...parts: (string | number | null | undefined)[]): string {
  return (
    parts
      .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
      .map((p) =>
        String(p)
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^A-Za-z0-9-]+/g, '-')
          .replace(/^-+|-+$/g, ''),
      )
      .join('-') + '.pdf'
  );
}
